import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, CircularProgress } from '@material-ui/core'
import { usePortfolioData } from '../PortfolioDataProvider'
import MeasurePickerDialog from '../MeasurePickerDialog'
import { buildMeasureSections } from '../buildMeasureSections'
import { buildLevelRequest, buildRowRequest } from './allocation/buildAllocationRequest'
import { useAllocationQuery } from './allocation/useAllocationQuery'
import AxisPicker from './scatter/AxisPicker'
import ScatterChart from './scatter/ScatterChart'
import ScatterDetailTable from './scatter/ScatterDetailTable'
import { deriveAxisOptions, isScatterEligible } from './scatter/scatterEligibility'

// Cross-sectional 2D comparison tool. Plots two measures against each
// other across the rows at a chosen grouping level.
//
// Reuses Allocation's request + query pipeline verbatim -- Allocation
// already emits a single multi-calc grouping request (one calc per
// `config.columns` entry) and flattens the response to per-row items
// with every measure keyed by columnInstance.id. Scatter consumes the
// same `items` shape and projects two of those measures onto the X / Y
// axes; the picker can swap axes without re-firing the request because
// the cached response already contains every column's value.

const useStyles = makeStyles(() => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 14
  },
  loading: {
    flex: 1,
    minHeight: 280,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13
  },
  error: {
    flex: 1,
    minHeight: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 24px',
    textAlign: 'center',
    color: 'var(--color-danger-600, #c62828)',
    fontSize: 13
  },
  empty: {
    flex: 1,
    minHeight: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 24px',
    textAlign: 'center',
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4
  }
}))

// Default axis selection priority:
//   1. payload.axisXColumnInstance / axisYColumnInstance (catalog presets)
//   2. payload.columnInstance (legacy right-click) -> Y default
//   3. First-eligible / second-eligible from the view
function eligibleAxisId (axisOptions, columnInstance) {
  if (!columnInstance?.id) return null
  return axisOptions.some(o => o.columnInstance.id === columnInstance.id) ? columnInstance.id : null
}

function pickDefaultAxes (axisOptions, payload) {
  if (axisOptions.length === 0) return { axisXId: null, axisYId: null }
  const explicitX = eligibleAxisId(axisOptions, payload?.axisXColumnInstance)
  const explicitY = eligibleAxisId(axisOptions, payload?.axisYColumnInstance)
  const payloadId = payload?.columnInstance?.id
  const payloadIsEligible = !!payloadId && axisOptions.some(o => o.columnInstance.id === payloadId)
  const axisYId = explicitY ??
    (payloadIsEligible
      ? payloadId
      : (axisOptions[1]?.columnInstance.id || axisOptions[0]?.columnInstance.id))
  const axisXId = explicitX ??
    (axisOptions.find(o => o.columnInstance.id !== axisYId)?.columnInstance.id ||
      axisOptions[0]?.columnInstance.id)
  return { axisXId, axisYId }
}

export default function ScatterTool ({ payload, compact, onPayloadChange }) {
  const classes = useStyles()
  const {
    config,
    asOfDate,
    columnDefinitions,
    prefs,
    clientId,
    entity,
    entityIds,
    selectedSubject
  } = usePortfolioData()

  // userExtras (measures picked at runtime via MeasurePickerDialog)
  // are persisted in payload.extraColumns -- the same array catalog
  // presets already use. We keep them in the catalog's extras bucket
  // rather than a separate `userExtras` slot so the dropdown's
  // "Suggested" section naturally includes both kinds of injection.
  // Local state mirrors so transient tiles still work.
  const [localExtras, setLocalExtras] = useState(() => payload?.extraColumns || [])
  const extraColumns = payload?.extraColumns || localExtras

  // Union view columns + catalog default axes + extras (catalog +
  // user) into a single column list.
  const chartColumns = useMemo(() => {
    const existing = config?.columns || []
    const preset = [
      payload?.axisXColumnInstance,
      payload?.axisYColumnInstance,
      ...extraColumns
    ].filter(Boolean)
    const merged = [...existing]
    for (const c of preset) {
      if (!merged.some(x => x.id === c.id)) merged.push(c)
    }
    return merged
  }, [config?.columns, payload?.axisXColumnInstance, payload?.axisYColumnInstance, extraColumns])

  const chartConfig = useMemo(
    () => ({ ...config, columns: chartColumns }),
    [config, chartColumns]
  )

  const axisOptions = useMemo(
    () => deriveAxisOptions(chartConfig, columnDefinitions, prefs),
    [chartConfig, columnDefinitions, prefs]
  )

  // Sectioned options for the AxisPicker. Provenance:
  //   "In this view"  -> the user's table columns (config.columns)
  //   "Suggested"     -> catalog default axes + extras (both kinds
  //                       live in extraColumns once persisted, so
  //                       there's no separate "Added" section here)
  const axisSections = useMemo(() => {
    const viewIds = (config?.columns || []).map(c => c.id)
    const suggestedIds = [
      payload?.axisXColumnInstance?.id,
      payload?.axisYColumnInstance?.id,
      ...extraColumns.map(c => c.id)
    ].filter(Boolean)
    const measureOptions = axisOptions.map(opt => ({ id: opt.columnInstance.id, label: opt.label }))
    return buildMeasureSections({ measureOptions, viewIds, suggestedIds, userIds: [] })
  }, [axisOptions, config?.columns, payload?.axisXColumnInstance, payload?.axisYColumnInstance, extraColumns])

  // Picker dialog state. `pickerAxis` is the axis the dialog will fill
  // when the user picks ('x' | 'y' | null). Handlers that close over
  // setAxisXId/setAxisYId are declared further below where those
  // setters exist.
  const [pickerAxis, setPickerAxis] = useState(null)
  const openMeasurePicker = useCallback((axis) => setPickerAxis(axis), [])
  const closeMeasurePicker = useCallback(() => setPickerAxis(null), [])

  // -- axis state --
  // Initial pick is payload-aware; subsequent picks live in tool state
  // until the payload identity changes. State is adjusted during render
  // via the "previous prop key" pattern -- on a payload identity flip
  // we synchronously setState during render, and React discards the
  // in-progress render and re-renders with the fresh defaults. Avoids
  // the wasted commit + effect cycle of a `useEffect` reset, and
  // collapses what used to be two effects (init/reset on payload,
  // recovery on options change) into linear render-time code.
  //
  // See React docs "Adjusting some state when a prop changes":
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [axisXId, setAxisXId] = useState(null)
  const [axisYId, setAxisYId] = useState(null)

  const payloadKey = useMemo(() => {
    if (!payload) return null
    const xId = payload.axisXColumnInstance?.id ?? ''
    const yId = payload.axisYColumnInstance?.id ?? payload.columnInstance?.id ?? ''
    if (payload.scope === 'header') return `header|${xId}|${yId}`
    if (payload.scope === 'row') {
      const rowKey = payload.row?.groupEntityId != null ? `id:${payload.row.groupEntityId}` : (payload.row?.name ?? '')
      return `row|${rowKey}|${xId}|${yId}`
    }
    if (payload.scope === 'level') return `level|${payload.depth ?? ''}|${xId}|${yId}`
    return null
  }, [payload])

  // Initialize / recover axis state.
  //
  // Two render-phase setState branches that MUST be mutually exclusive:
  //
  // 1. Init / payload-changed: pull from `pickDefaultAxes(axisOptions,
  //    payload)` so the catalog's `axisXColumnInstance` /
  //    `axisYColumnInstance` preset reaches the picker on first mount
  //    and on any subsequent payload identity change.
  // 2. Recovery: if the user edits config.columns out from under the
  //    tool, snap stale axis ids back to a valid option.
  //
  // Why mutually exclusive: non-functional setState calls inside one
  // render are batched and the LAST call wins. If both branches fired
  // in the same render the recovery's first-eligible fallback would
  // clobber the catalog default (the original bug -- saved Risk vs
  // Return tile rendered as Market Value vs Market Value because the
  // recovery overwrote `setAxisXId(STDDEV.id)` with
  // `setAxisXId(firstEligible.id)`).
  //
  // `prevPayloadKey` is initialized to `null` (not the current
  // payloadKey) so init also fires on first mount.
  const [prevPayloadKey, setPrevPayloadKey] = useState(null)
  const payloadJustChanged = payloadKey !== prevPayloadKey
  if (axisOptions.length > 0) {
    if (payloadJustChanged) {
      setPrevPayloadKey(payloadKey)
      const defaults = pickDefaultAxes(axisOptions, payload)
      setAxisXId(defaults.axisXId)
      setAxisYId(defaults.axisYId)
    } else {
      const xValid = axisXId && axisOptions.some(o => o.columnInstance.id === axisXId)
      if (!xValid) {
        const xFallback = axisOptions.find(o => o.columnInstance.id !== axisYId)?.columnInstance.id ?? axisOptions[0].columnInstance.id
        if (xFallback !== axisXId) setAxisXId(xFallback)
      }
      const yValid = axisYId && axisOptions.some(o => o.columnInstance.id === axisYId)
      if (!yValid) {
        const yFallback = axisOptions.find(o => o.columnInstance.id !== axisXId)?.columnInstance.id ?? axisOptions[0].columnInstance.id
        if (yFallback !== axisYId) setAxisYId(yFallback)
      }
    }
  }

  // Helper -- find the full column instance for an id so we can
  // persist the instance (not just the id) into the payload. Axes
  // are picked by id in the dropdown but the persisted shape is the
  // full instance so a reload can resolve it without re-deriving.
  const instanceForId = useCallback((id) => (
    chartColumns.find(c => c.id === id) || null
  ), [chartColumns])

  // Fires when MeasurePickerDialog returns a configured column
  // instance. Add it to extras (so it stays in the picker for
  // swapping), set as the active axis, and persist into payload.
  //
  // Mutual exclusion: the picker dialog has no `excludeId` concept,
  // so a user could pick an instance that's currently on the OTHER
  // axis. Mirror the same bump-the-other-axis logic that
  // handleAxisXChange / handleAxisYChange enforce so the chart can't
  // end up in an X==Y state.
  const handleMeasurePicked = useCallback((instance) => {
    if (!instance) return
    const nextExtras = extraColumns.some(c => c.id === instance.id)
      ? extraColumns
      : [...extraColumns, instance]
    setLocalExtras(nextExtras)
    const patch = { extraColumns: nextExtras }
    if (pickerAxis === 'x') {
      setAxisXId(instance.id)
      patch.axisXColumnInstance = instance
      if (instance.id === axisYId) {
        const fallback = axisOptions.find(o => o.columnInstance.id !== instance.id)?.columnInstance.id
        if (fallback) {
          setAxisYId(fallback)
          patch.axisYColumnInstance = instanceForId(fallback)
        }
      }
    } else if (pickerAxis === 'y') {
      setAxisYId(instance.id)
      patch.axisYColumnInstance = instance
      if (instance.id === axisXId) {
        const fallback = axisOptions.find(o => o.columnInstance.id !== instance.id)?.columnInstance.id
        if (fallback) {
          setAxisXId(fallback)
          patch.axisXColumnInstance = instanceForId(fallback)
        }
      }
    }
    if (onPayloadChange) onPayloadChange(patch)
  }, [extraColumns, pickerAxis, axisXId, axisYId, axisOptions, instanceForId, onPayloadChange])

  // Enforce mutual exclusion at the change handler level (in addition
  // to the picker's disabled-options UI). If the user picks X = Y,
  // bump the other axis to the next available option. Persists both
  // axis changes into payload so a reload restores them.
  const handleAxisXChange = useCallback((nextX) => {
    setAxisXId(nextX)
    const patch = { axisXColumnInstance: instanceForId(nextX) }
    if (nextX === axisYId) {
      const fallback = axisOptions.find(o => o.columnInstance.id !== nextX)?.columnInstance.id
      if (fallback) {
        setAxisYId(fallback)
        patch.axisYColumnInstance = instanceForId(fallback)
      }
    }
    if (onPayloadChange) onPayloadChange(patch)
  }, [axisYId, axisOptions, instanceForId, onPayloadChange])

  const handleAxisYChange = useCallback((nextY) => {
    setAxisYId(nextY)
    const patch = { axisYColumnInstance: instanceForId(nextY) }
    if (nextY === axisXId) {
      const fallback = axisOptions.find(o => o.columnInstance.id !== nextY)?.columnInstance.id
      if (fallback) {
        setAxisXId(fallback)
        patch.axisXColumnInstance = instanceForId(fallback)
      }
    }
    if (onPayloadChange) onPayloadChange(patch)
  }, [axisXId, axisOptions, instanceForId, onPayloadChange])

  // Subject wins over the catalog payload's default scope.
  const built = useMemo(() => {
    if (!payload || !chartConfig || !asOfDate || !entity || !entityIds) return null
    if (selectedSubject?.kind === 'row') {
      return buildRowRequest({ config: chartConfig, asOfDate, entity, entityIds, row: selectedSubject.row })
    }
    if (selectedSubject?.kind === 'level') {
      return buildLevelRequest({ config: chartConfig, asOfDate, entity, entityIds, depth: selectedSubject.depth })
    }
    if (payload.scope === 'header') {
      return buildLevelRequest({ config: chartConfig, asOfDate, entity, entityIds, depth: 0 })
    }
    if (payload.scope === 'level') {
      return buildLevelRequest({ config: chartConfig, asOfDate, entity, entityIds, depth: payload.depth })
    }
    if (payload.scope === 'row') {
      return buildRowRequest({ config: chartConfig, asOfDate, entity, entityIds, row: payload.row })
    }
    return null
  }, [payload, chartConfig, asOfDate, entity, entityIds, selectedSubject])

  const request = built?.request || null
  const unwrap = built?.unwrap || null

  const globalDateConfig = useMemo(() => ({
    dateMode: config?.dateMode,
    quickDate: config?.quickDate,
    startDate: config?.startDate,
    endDate: config?.endDate
  }), [config?.dateMode, config?.quickDate, config?.startDate, config?.endDate])

  const { items, isFetching, error } = useAllocationQuery({
    request,
    clientId,
    columnInstances: chartColumns,
    globalDateConfig,
    entity,
    entityIds,
    unwrap
  })

  // -- axis meta lookups --
  const axisXMeta = useMemo(
    () => axisOptions.find(o => o.columnInstance.id === axisXId) || null,
    [axisOptions, axisXId]
  )
  const axisYMeta = useMemo(
    () => axisOptions.find(o => o.columnInstance.id === axisYId) || null,
    [axisOptions, axisYId]
  )

  // -- project items into [{ name, groupId, x, y }] --
  // Items have all measures keyed by columnInstance.id. Rows with both
  // X and Y numeric become chart points; rows missing either still
  // appear in the detail table (with -- placeholders) so the user can
  // see why a name they expected isn't on the chart.
  const allRows = useMemo(() => {
    if (!items?.length || !axisXId || !axisYId) return []
    return items.map(it => ({
      name: it.name,
      groupId: it.groupId,
      x: typeof it[axisXId] === 'number' ? it[axisXId] : null,
      y: typeof it[axisYId] === 'number' ? it[axisYId] : null
    }))
  }, [items, axisXId, axisYId])

  const chartPoints = useMemo(
    () => allRows.filter(r => typeof r.x === 'number' && typeof r.y === 'number'),
    [allRows]
  )

  // -- render --
  // Toolbar always renders so the picker stays reachable even when
  // the current axes produced no chart (no measures, no grouping,
  // error, etc.). See AllocationTool for the same pattern.
  let chartContent = null
  if (axisOptions.length < 2) {
    chartContent = (
      <div className={classes.empty}>
        Add at least two comparable measures to your view to compare them on a scatter chart.
      </div>
    )
  } else if (!request) {
    chartContent = (
      <div className={classes.empty}>
        Add a grouping to your view to compare across groups.
      </div>
    )
  } else if (error) {
    chartContent = (
      <div className={classes.error}>
        <span>Could not load comparison.</span>
        <span>{error?.message || 'Unknown error'}</span>
      </div>
    )
  } else if (isFetching && allRows.length === 0) {
    chartContent = (
      <div className={classes.loading}>
        <CircularProgress size={18} />
        <span>Loading comparison...</span>
      </div>
    )
  } else if (!axisXMeta || !axisYMeta) {
    chartContent = (
      <div className={classes.empty}>
        Pick two measures from the toolbar to compare.
      </div>
    )
  } else {
    chartContent = (
      <>
        <ScatterChart
          points={chartPoints}
          axisXLabel={axisXMeta.label}
          axisYLabel={axisYMeta.label}
          axisXFormat={axisXMeta.valueFormat}
          axisYFormat={axisYMeta.valueFormat}
          height={340}
        />
        {!compact && (
          <ScatterDetailTable
            rows={allRows}
            axisXLabel={axisXMeta.label}
            axisYLabel={axisYMeta.label}
            axisXFormat={axisXMeta.valueFormat}
            axisYFormat={axisYMeta.valueFormat}
          />
        )}
      </>
    )
  }

  return (
    <div className={classes.body}>
      <div className={classes.toolbar}>
        <AxisPicker
          axisSections={axisSections}
          axisXId={axisXId}
          axisYId={axisYId}
          onAxisXChange={handleAxisXChange}
          onAxisYChange={handleAxisYChange}
          onMoreX={() => openMeasurePicker('x')}
          onMoreY={() => openMeasurePicker('y')}
        />
      </div>
      <MeasurePickerDialog
        open={pickerAxis !== null}
        onClose={closeMeasurePicker}
        onPick={handleMeasurePicked}
        title={pickerAxis === 'x' ? 'Pick X axis measure' : 'Pick Y axis measure'}
        isEligible={isScatterEligible}
      />
      {chartContent}
    </div>
  )
}

ScatterTool.propTypes = {
  compact: PropTypes.bool,
  onPayloadChange: PropTypes.func,
  payload: PropTypes.shape({
    scope: PropTypes.oneOf(['header', 'level', 'row']).isRequired,
    columnInstance: PropTypes.object,
    row: PropTypes.object,
    depth: PropTypes.number,
    groupingLabel: PropTypes.string,
    axisXColumnInstance: PropTypes.shape({
      id: PropTypes.string.isRequired,
      columnType: PropTypes.string
    }),
    axisYColumnInstance: PropTypes.shape({
      id: PropTypes.string.isRequired,
      columnType: PropTypes.string
    }),
    // Catalog-injected extra eligible columns. Surfaced in the axis
    // pickers alongside the user's view columns; not used for the
    // default X/Y pick (only `axisXColumnInstance` / `axisYColumnInstance`
    // are).
    extraColumns: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      columnType: PropTypes.string
    }))
  })
}
